import { customerStatusLabel, customerStatusTone } from './status-labels.js';
import { memberships } from './tenant-access.js';
import { toNavClaims } from './navigation.js';

// The customer switcher's options (portal workbench spec, "Security",
// "Frontend state"): a customer user sees the tenant the token carries, or,
// for a user of several customers, the customers GET /me lists for them
// (ENTRAID-307); an operator uses the existing operator-only GET /customers
// list. No new endpoint.

function toOption(customerId, name, status) {
  return {
    customerId,
    name: name || customerId,
    status: status ?? null,
    statusLabel: status ? customerStatusLabel(status) : null,
    tone: status ? customerStatusTone(status) : null,
  };
}

/** @returns {Array<{ customerId: string, name: string, status: string|null, statusLabel: string|null, tone: string|null }>} */
export function customerOptions({ me = null, navClaims, impersonating = null, operatorCustomers = null }) {
  if (navClaims?.isOperator === true) {
    return (operatorCustomers ?? [])
      .map((c) => toOption(c.customerId, c.companyName, c.status))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  // A user of several customers (ENTRAID-307): every customer GET /me lists, each with its own claims, so the switcher
  // sends them to a section they can open there.
  const several = memberships(me);
  if (several.length > 0 && !impersonating?.customerId) {
    return several
      .map((m) => ({ ...toOption(m.customerId, m.companyName, null), navClaims: toNavClaims(me, null, m.customerId) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  const id = navClaims?.customerId;
  if (typeof id !== 'string' || id.length === 0) return [];
  const own = me?.customer?.customerId === id ? me.customer : null;
  const viewAsName = impersonating?.customerId === id ? impersonating.companyName : null;
  return [toOption(id, own?.companyName || viewAsName, own?.status ?? null)];
}

/** Case-insensitive match on the name or the id; an empty query keeps all. */
export function filterCustomerOptions(options, query) {
  const q = String(query ?? '').trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.name.toLowerCase().includes(q) || o.customerId.toLowerCase().includes(q));
}

/**
 * Every page of GET /customers. A list that runs past maxPages is an error,
 * never a silently partial list: the operator must not pick from a fleet that
 * is missing customers. A page that is not { items: [...] } is an error too.
 */
export async function fetchAllCustomers(listCustomers, { maxPages = 20 } = {}) {
  const items = [];
  let cursor;
  for (let page = 0; page < maxPages; page += 1) {
    const res = await listCustomers(cursor);
    // A page without an items array is a broken answer, not an empty fleet.
    if (!Array.isArray(res?.items)) throw new Error('The customer list answer was malformed.');
    items.push(...res.items);
    cursor = res?.cursor;
    if (!cursor) break;
  }
  if (cursor) throw new Error(`The customer list has more than ${maxPages} pages; refusing to show an incomplete list.`);
  return items;
}
